import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/browser";
import { getApiUrl } from "@utils/baseUrl";
import LocalStorageService from "@core/services/local-storage.service";
import { streamStarted, conversationStarted, textDeltaAppended, textReset, toolCallStarted, toolCallResulted, structuredBlockReceived, companiesReceived, followUpsReceived, streamEnded, streamErrored, streamAborted, streamInProgressDetected } from "@redux/Hermes/hermesSlice";
import { rememberLastSentConversation } from "@containers/Hermes/utils/lastConversation";
import useHermesAnalytics from "@containers/Hermes/hooks/useHermesAnalytics";
import type { AppDispatch, SendMessage, AbortStream, HermesErrorCode } from "@containers/Hermes/types";

const localStorageService = LocalStorageService.getService();

type ParsedSseBlock = { eventName: string; payload: any };

function parseSseBlock(raw: string): ParsedSseBlock | null {
  let eventName: string | null = null;
  let dataStr: string | null = null;

  for (const line of raw.split("\n")) {
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataStr = line.slice(5).trim();
    }
  }

  if (!eventName || !dataStr) return null;

  try {
    return { eventName, payload: JSON.parse(dataStr) };
  } catch (e: any) {
    Sentry.captureException(new Error(`Hermes SSE: failed to parse data for event "${eventName}": ${e.message}`));
    return null;
  }
}

export default function useHermesStream(): { sendMessage: SendMessage; abort: AbortStream } {
  const dispatch = useDispatch<AppDispatch>();
  const analytics = useHermesAnalytics();
  const abortControllerRef = useRef<AbortController | null>(null);

  const abort = useCallback<AbortStream>(() => {
    if (!abortControllerRef.current) return;
    abortControllerRef.current.abort();
    abortControllerRef.current = null;
    dispatch(streamAborted());
  }, [dispatch]);

  const sendMessage = useCallback<SendMessage>(
    async (text, conversationId = null, confirmToken) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const isNewConversation = !conversationId;

      // Follow-up into an existing conversation: re-establish the bookmark now.
      // The bookmark is dropped once the user has viewed a finished turn, and a
      // follow-up starts a fresh in-progress turn that must survive a reload —
      // but conversation_started (which sets the bookmark for new conversations)
      // only fires for brand-new conversations, so set it here for follow-ups.
      if (conversationId) {
        rememberLastSentConversation(conversationId);
      }

      dispatch(streamStarted({ userMessageText: text, conversationId }));
      analytics.messageSent({ conversationId, isNewConversation, charLength: text.length });

      const requestBody: { message: string; conversation_id?: string; confirm_token?: string } = { message: text };
      if (conversationId) requestBody.conversation_id = conversationId;
      if (confirmToken) requestBody.confirm_token = confirmToken;

      let doneEventReceived = false;

      try {
        const response = await fetch(getApiUrl("hermes/chat/stream"), {
          method: "POST",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            sessionId: localStorageService.getSessionID(),
            ssoToken: localStorageService.getAccessToken(),
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          // 409 stream_in_progress: a previous response for this conversation is
          // still being generated in the background. Don't show a generic error —
          // roll back the optimistic message and poll until it lands. (A brand-new
          // conversation has no prior stream, so there's nothing to poll for.)
          if (response.status === 409 && conversationId) {
            dispatch(streamInProgressDetected({ conversationId }));
            return false;
          }

          let errorMessage = `Request failed (${response.status})`;
          let code: HermesErrorCode | undefined;

          try {
            const errorBody = await response.json();
            errorMessage = errorBody.message ?? errorBody.error ?? errorMessage;
            if (typeof errorBody.code === "string") code = errorBody.code as HermesErrorCode;
          } catch {
            errorMessage = `Request failed (${response.status})`;
          }

          dispatch(streamErrored({ message: errorMessage, code }));
          // A 401 is an expired session, not a failed Hermes response.
          if (response.status !== 401) analytics.responseErrored({ conversationId, errorCode: code, errorMessage, phase: "http" });
          return true;
        }

        // A 200 with no body shouldn't happen for the SSE endpoint, but guard so
        // it surfaces as an error rather than a TypeError on getReader().
        if (!response.body) {
          dispatch(streamErrored({ message: "Connection error. Please try again." }));
          return true;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        for (;;) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const blocks = buffer.split("\n\n");
          buffer = blocks.pop() ?? "";

          for (const block of blocks) {
            if (!block.trim()) continue;

            const parsed = parseSseBlock(block);
            if (!parsed) continue;

            const { eventName, payload } = parsed;

            switch (eventName) {
              case "conversation_started":
                dispatch(conversationStarted(payload));
                // Remember this conversation as soon as we have an id — the
                // URL only picks it up once the turn finishes (see Hermes.tsx),
                // so a reload mid-stream would otherwise strand the user on a
                // blank new conversation despite the backend still working.
                if (payload?.conversation_id) {
                  rememberLastSentConversation(payload.conversation_id);
                }
                break;

              case "text":
                dispatch(textDeltaAppended(payload));
                break;

              case "text_reset":
                dispatch(textReset());
                break;

              case "tool_call_start":
                dispatch(toolCallStarted(payload));
                break;

              case "tool_call_result":
                dispatch(toolCallResulted(payload));
                break;

              case "structured_block":
                dispatch(structuredBlockReceived(payload));
                break;

              case "companies":
                dispatch(companiesReceived(payload));
                break;

              case "follow_up_suggestions":
                dispatch(followUpsReceived(payload));
                break;

              case "done":
                doneEventReceived = true;
                dispatch(streamEnded(payload));
                // The backend generates the title server-side for a new conversation's
                // first turn; a missing title here means that step failed silently on
                // its end. No client retry — just surface it so we notice.
                if (isNewConversation && !payload.title) {
                  Sentry.captureMessage("Hermes: done event for new conversation missing generated title", { level: "warning", extra: { conversationId: payload.conversation_id } });
                }
                break;

              case "error":
                dispatch(streamErrored(payload));
                analytics.responseErrored({ conversationId, errorCode: payload?.code, errorMessage: payload?.message, phase: "stream" });
                break;

              default:
                break;
            }
          }
        }

        if (!doneEventReceived) {
          dispatch(streamAborted());
        }
        return true;
      } catch (error: any) {
        if (error.name === "AbortError") {
          return true;
        }

        Sentry.captureException(error);
        dispatch(streamErrored({ message: "Connection error. Please try again." }));
        analytics.responseErrored({ conversationId, errorMessage: error?.message, phase: "network" });
        return true;
      } finally {
        abortControllerRef.current = null;
      }
    },
    [dispatch, analytics]
  );

  return { sendMessage, abort };
}
